import React, { useState } from 'react';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { TrendingUp, FileSpreadsheet, FileText, Loader2, AlertTriangle, Tags } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STOP = new Set(['the', 'and', 'for', 'was', 'were', 'our', 'their', 'they', 'that', 'this', 'with', 'from',
  'have', 'has', 'had', 'not', 'but', 'you', 'your', 'out', 'due', 'them', 'there', 'because', 'lost', 'lose',
  'losing', 'rfp', 'deal', 'did', 'didn', 'didnt', 'are', 'its', 'who', 'what', 'when', 'why', 'how', 'too',
  'very', 'much', 'more', 'less', 'over', 'under', 'into', 'about', 'been', 'being', 'would', 'could', 'should',
  'then', 'than', 'also', 'just', 'only', 'all', 'any', 'some', 'most', 'each', 'other', 'another', 'which',
  'while', 'will', 'won', 'client']);

const themesFrom = (notes) => {
  const counts = {};
  notes.forEach(n => {
    new Set((n.toLowerCase().match(/[a-z][a-z'-]{2,}/g) || []).filter(w => !STOP.has(w)))
      .forEach(w => { counts[w] = (counts[w] || 0) + 1; });
  });
  const all = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const recurring = all.filter(([, c]) => c >= 2).slice(0, 8);
  return recurring.length ? recurring : all.slice(0, 4);
};

const quarterKey = (iso) => {
  const d = new Date(iso);
  return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
};
const quarterOrd = (k) => {
  const [q, y] = k.split(' ');
  return Number(y) * 4 + Number(q[1]);
};

export const RfpInsights = ({ projects }) => {
  const [exporting, setExporting] = useState(false);
  const closed = projects.filter(p => ['won', 'lost'].includes(p.outcome));
  const conf = (p) => p.response_score?.overall?.confidence_score;
  const won = closed.filter(p => p.outcome === 'won');
  const scored = closed.filter(p => conf(p) != null);
  const wonConf = won.filter(p => conf(p) != null).map(conf);
  const lostConf = closed.filter(p => p.outcome === 'lost' && conf(p) != null).map(conf);
  const avg = (list) => (list.length ? Math.round(list.reduce((a, v) => a + v, 0) / list.length) : null);
  const scoreOf = (p) => p.client_score_avg;
  const wonScores = won.filter(p => scoreOf(p) != null).map(scoreOf);
  const lostScores = closed.filter(p => p.outcome === 'lost' && scoreOf(p) != null).map(scoreOf);
  const sAvg = (list) => (list.length ? +(list.reduce((a, v) => a + v, 0) / list.length).toFixed(1) : null);
  const overlap = wonConf.length > 0 && lostConf.length > 0 && Math.min(...wonConf) <= Math.max(...lostConf);
  const threshold = overlap ? Math.round((avg(wonConf) + avg(lostConf)) / 2) : null;
  const themes = themesFrom(closed.filter(p => p.outcome === 'lost' && p.loss_note).map(p => p.loss_note));
  const byQuarter = {};
  closed.filter(p => p.outcome === 'lost' && p.loss_note).forEach(p => {
    const k = quarterKey(p.outcome_changed_at || p.due_date || p.created_at || new Date().toISOString());
    (byQuarter[k] = byQuarter[k] || []).push(p.loss_note);
  });
  const trendRows = Object.entries(byQuarter).sort((a, b) => quarterOrd(a[0]) - quarterOrd(b[0])).slice(-4);
  const W = 300, H = 34;

  const exportInsights = async (kind) => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_URL}/api/rfp/response/insights/export/${kind}`,
        { withCredentials: true, responseType: 'blob' });
      saveAs(res.data, `RFP-Win-Insights.${kind}`);
    } catch { toast.error('Could not export insights'); }
    finally { setExporting(false); }
  };

  if (!closed.length) return null;
  return (
    <div className="border border-slate-200 bg-white rounded-md p-3 space-y-2" data-testid="rfp-win-insights">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
          <TrendingUp className="w-3.5 h-3.5 text-[#4ABFCF]" />Win insights
        </p>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-slate-500" disabled={exporting}
            onClick={() => exportInsights('xlsx')} title="Export win insights + loss notes to Excel"
            data-testid="rfp-insights-export-xlsx">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3 mr-0.5" />}Excel
          </Button>
          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 text-slate-500" disabled={exporting}
            onClick={() => exportInsights('docx')} title="Export win insights + loss notes to Word"
            data-testid="rfp-insights-export-docx">
            {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3 mr-0.5" />}Word
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600">
        <span><b className="text-[#0F2040] text-base">{Math.round((won.length / closed.length) * 100)}%</b> win rate ({won.length}/{closed.length})</span>
        {avg(wonConf) != null && (
          <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">avg confidence when won: {avg(wonConf)}%</Badge>
        )}
        {avg(lostConf) != null && (
          <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200">when lost: {avg(lostConf)}%</Badge>
        )}
      </div>
      {(sAvg(wonScores) != null || sAvg(lostScores) != null) && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="rfp-insights-client-scores">
          <span className="text-[10px] font-semibold text-slate-500">Client scale fit at close:</span>
          {sAvg(wonScores) != null && (
            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200"
              title={`Average client score across ${wonScores.length} won deal${wonScores.length === 1 ? '' : 's'} with client scoring`}
              data-testid="rfp-insights-score-won">avg client score when won: {sAvg(wonScores)}</Badge>
          )}
          {sAvg(lostScores) != null && (
            <Badge variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200"
              title={`Average client score across ${lostScores.length} lost deal${lostScores.length === 1 ? '' : 's'} with client scoring`}
              data-testid="rfp-insights-score-lost">when lost: {sAvg(lostScores)}</Badge>
          )}
          {sAvg(wonScores) != null && sAvg(lostScores) != null && sAvg(wonScores) > sAvg(lostScores) && (
            <span className="text-[10px] text-slate-400" data-testid="rfp-insights-score-hint">
              deals averaging under {(((sAvg(wonScores) + sAvg(lostScores)) / 2)).toFixed(1)} on the client scale tend to lose
            </span>
          )}
        </div>
      )}
      {scored.length > 0 && (
        <div className="flex items-center gap-2">
          <svg width={W} height={H} data-testid="rfp-win-scatter">
            <line x1="4" y1={H / 2} x2={W - 4} y2={H / 2} stroke="#E2E8F0" />
            {[0, 50, 100].map(v => (
              <text key={v} x={4 + (v / 100) * (W - 8)} y={H - 1} fontSize="9" fill="#64748B" textAnchor="middle">{v}%</text>
            ))}
            {scored.map((p, i) => (
              <circle key={p.id} cx={4 + (conf(p) / 100) * (W - 8)} cy={H / 2 + (i % 2 === 0 ? -4 : 4)} r="4"
                fill={p.outcome === 'won' ? '#10B981' : '#F43F5E'} fillOpacity="0.8">
                <title>{p.name} — {conf(p)}% · {p.outcome}{p.loss_note ? ` · ${p.loss_note}` : ''}</title>
              </circle>
            ))}
          </svg>
          <span className="text-[9px] text-slate-400">confidence score at close — <span className="text-emerald-600">won</span> / <span className="text-rose-500">lost</span></span>
        </div>
      )}
      {threshold != null && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-2.5 py-1.5 flex items-start gap-1.5"
          data-testid="rfp-calibration-hint">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-500" />
          <span>Won and lost deals overlap in confidence — treat scores under <b>{threshold}%</b> as at-risk and review those responses before submission.</span>
        </p>
      )}
      {(() => {
        const cnt = {};
        closed.filter(p => p.outcome === 'lost').forEach(p => (p.loss_reasons || []).forEach(t => { cnt[t] = (cnt[t] || 0) + 1; }));
        const lostN = closed.filter(p => p.outcome === 'lost').length;
        const rows = Object.entries(cnt).sort((a, b) => b[1] - a[1]);
        if (!rows.length) return null;
        return (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="rfp-loss-reason-breakdown">
            <span className="text-[10px] font-semibold text-rose-500 flex items-center gap-1">
              <Tags className="w-3 h-3 text-rose-400" />Losses by reason:
            </span>
            {rows.map(([t, n]) => (
              <span key={t} className="text-[9px] bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5"
                title={`${n} of ${lostN} losses tagged "${t}"`}>
                {t} ×{n} ({Math.round((n / lostN) * 100)}%)
              </span>
            ))}
          </div>
        );
      })()}
      {(() => {
        const byQ = {};
        closed.filter(p => p.outcome === 'lost' && (p.loss_reasons || []).length).forEach(p => {
          const k = quarterKey(p.outcome_changed_at || p.due_date || p.created_at || new Date().toISOString());
          const d = (byQ[k] = byQ[k] || {});
          p.loss_reasons.forEach(t => { d[t] = (d[t] || 0) + 1; });
        });
        const qs = Object.keys(byQ).sort((a, b) => quarterOrd(a) - quarterOrd(b)).slice(-4);
        if (qs.length < 2) return null;
        const prev = byQ[qs[qs.length - 2]];
        const last = byQ[qs[qs.length - 1]];
        const growing = Object.keys(last).filter(t => last[t] > (prev[t] || 0));
        return (
          <div className="space-y-0.5" data-testid="rfp-loss-reason-trend">
            <p className="text-[10px] font-semibold text-slate-500">Loss reasons by quarter</p>
            {qs.map(k => (
              <p key={k} className="text-[10px] text-slate-500" data-testid={`rfp-loss-reason-trend-${k.replace(' ', '-')}`}>
                <span className="font-semibold text-[#0F2040]">{k}</span>
                <span className="text-slate-400"> · </span>
                {Object.entries(byQ[k]).sort((a, b) => b[1] - a[1]).map(([t, n]) => `${t} ×${n}`).join(', ')}
              </p>
            ))}
            {growing.length > 0 && (
              <p className="text-[10px] text-rose-600 font-semibold" data-testid="rfp-loss-reason-growing">
                ▲ Growing vs previous quarter: {growing.join(', ')}
              </p>
            )}
          </div>
        );
      })()}
      {themes.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="rfp-loss-themes">
          <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
            <Tags className="w-3 h-3 text-rose-400" />Loss reason themes:
          </span>
          {themes.map(([w, c]) => (
            <Badge key={w} variant="outline" className="text-[9px] bg-rose-50 text-rose-700 border-rose-200"
              data-testid={`rfp-loss-theme-${w}`}>{w}{c > 1 ? ` ×${c}` : ''}</Badge>
          ))}
        </div>
      )}
      {trendRows.length > 1 && (
        <div className="space-y-0.5" data-testid="rfp-loss-trends">
          <p className="text-[10px] font-semibold text-slate-500">Loss themes by quarter</p>
          {trendRows.map(([q, notes]) => (
            <p key={q} className="text-[10px] text-slate-500" data-testid={`rfp-loss-trend-${q.replace(' ', '-')}`}>
              <span className="font-semibold text-[#0F2040]">{q}</span>
              <span className="text-slate-400"> · {notes.length} lost — </span>
              {themesFrom(notes).slice(0, 5).map(([w, c]) => `${w}${c > 1 ? ` ×${c}` : ''}`).join(', ') || '—'}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};
